export type BenchType = 'CONSULTANT' | 'APPLICANT' | 'FREELANCER';
export const BENCH_TYPES: readonly BenchType[] = ['CONSULTANT', 'APPLICANT', 'FREELANCER'];

/** True nur für einen bekannten Bench-Typ; schützt die Typ-Badge vor null/leeren/unbekannten Werten aus dem Backend (NP20-871). */
export function isBenchType(value: string | null | undefined): value is BenchType {
  return value != null && (BENCH_TYPES as readonly string[]).includes(value);
}

/**
 * Kategorien eines Eintrags (NP20-874). Bewerber und Freelancer sind im Profil unabhängig, ein
 * Berater kann beides sein — dann kommen beide zurück. Ohne gesetzte Schalter gilt der
 * Legacy-Einzelwert type, damit Alt-Einträge und ein noch nicht aktualisiertes Backend passen.
 */
export function benchCategories(entry: Pick<BenchEntryDTO, 'type' | 'applicant' | 'freelancer'>): BenchType[] {
  const categories: BenchType[] = [];
  if (entry.applicant) categories.push('APPLICANT');
  if (entry.freelancer) categories.push('FREELANCER');
  if (categories.length) return categories;
  return isBenchType(entry.type) ? [entry.type] : [];
}

export type BenchFirm = string;

export function isBenchFirm(value: string | null | undefined): value is BenchFirm {
  return typeof value === 'string' && value.length > 0;
}

export type RequestStatus = 'OPEN' | 'FEEDBACK' | 'ACCEPTED' | 'PROJECT_CREATED' | 'REJECTED' | 'CLOSED';
export const REQUEST_STATUSES: readonly RequestStatus[] =
  ['OPEN', 'FEEDBACK', 'ACCEPTED', 'PROJECT_CREATED', 'REJECTED', 'CLOSED'];

export function isRequestStatus(value: string | null | undefined): value is RequestStatus {
  return value != null && (REQUEST_STATUSES as readonly string[]).includes(value);
}

export type RequestPrio = 'HIGH' | 'MEDIUM' | 'LOW';
export type VolumeUnit = 'DAYS' | 'HOURS';
/** NP20-1216: Pflichtangaben am Angebot. */
export type OfferRequiredField = 'role' | 'hourlyRateRemote' | 'hourlyRateOnSite';

export interface NoteDTO {
  id: string | null;
  date: string | null;
  author: string;
  text: string;
  isMeetingNote: boolean;
}

/**
 * Metadaten des Angebots-Anhangs (NP20-814). Nur `note` ist beschreibbar; alle
 * uebrigen Felder vergibt der Server beim Upload. Der Bench-Entry-PUT uebernimmt
 * ausschliesslich die Notiz und verwirft einen Anhang, zu dem serverseitig keiner
 * gespeichert ist.
 */
export interface OfferAttachmentDTO {
  fileName: string | null;
  note: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedBy: string | null;
  uploadedAt: string | null;
}

export interface OfferDTO {
  /**
   * Vom Server vergeben. Die Anhang-Endpoints adressieren darueber, und der
   * Bench-Entry-PUT erkennt daran bestehende Angebote — ein Angebot ohne oder mit
   * unbekannter id gilt als neu und verliert seinen Anhang.
   */
  id: string | null;
  date: string | null;
  submittedBy: string;
  submittedByUserId: string | null;
  referenceNumber: string;
  /** Optionale externe Ref-Nr. (NP20-813/812), z. B. vom Vermittler/Kunden. */
  externalReferenceNumber: string | null;
  role: string;
  customer: string;
  intermediary: string;
  businessUnitId: string | null;
  status: RequestStatus;
  priority: RequestPrio;
  probability: number | null;
  /** Zusage-Datum (NP20-807/806): nur bei Status ACCEPTED ("Zusage") gepflegt, sonst null. */
  commitmentFrom: string | null;
  /** NP20-1238: Projektverlängerung statt Neuprojekt; das Datum gibt es nur bei Verlängerung. */
  extension: boolean;
  extendedUntil: string | null;
  /**
   * Im Dialog zum Status „Projekt angelegt" abgefragt: Start und Ende werden zu Beginn und Ende
   * des automatisch angelegten Projekts, das Volumen wird beim Berater gespeichert. Optional, weil
   * eine Antwort ohne die Felder die Keys gar nicht traegt.
   */
  projectStart?: string | null;
  projectEnd?: string | null;
  extendProjectUuid?: string | null;
  volume?: number | null;
  volumeUnit?: VolumeUnit | null;
  interviewPdl: string;
  interviewCustomer: string;
  hourlyRateRemote: number | null;
  hourlyRateOnSite: number | null;
  hubspotUrl: string;
  notes: NoteDTO[];
  attachment: OfferAttachmentDTO | null;
  projectAutomationTriggered?: boolean;
  applyChangesToProject?: boolean;
}

export interface BusinessUnitDTO {
  id: string;
  key: string;
  name: string;
  color: string;
  bg: string;
  ring: string;
  active: boolean;
  usageCount: number;
}

export interface CreateBusinessUnitDTO {
  key?: string;
  name: string;
  color?: string;
  bg?: string;
  ring?: string;
}

/** PATCH /api/bench/entries/{id}: fehlender Key = unveraendert, null = leeren. */
export interface BenchEntryPatch {
  availableFrom?: string | null;
  nextStep?: string | null;
}

export interface UpdateBusinessUnitDTO {
  key?: string;
  name?: string;
  color?: string;
  bg?: string;
  ring?: string;
}

export interface BusinessUnitActivationDTO {
  active: boolean;
  targetBusinessUnitId?: string;
}

/**
 * Business Units alphabetisch nach dem angezeigten Kürzel (NP20-778). Ohne das erscheinen sie in
 * der Reihenfolge des Backends, die für den Lesenden willkürlich wirkt. Nicht mutierend, damit die
 * Antwort des Service für andere Verbraucher unverändert bleibt.
 */
export function businessUnitsByKey(units: BusinessUnitDTO[]): BusinessUnitDTO[] {
  return [...(units ?? [])].sort((a, b) => (a.key ?? '').localeCompare(b.key ?? ''));
}

export interface BenchEntryDTO {
  id: string;
  number: number;
  version: number;
  consultantId: string | null;
  /** Primäres Profilfoto des verknüpften Beraters (NP20-804/BE); null = kein Foto → Initialen-Fallback. */
  photoId: string | null;
  fullName: string;
  abbreviation: string | null;
  /** Legacy-Einzelwert: trägt nur eine Kategorie. Für Anzeige und Filter benchCategories() nutzen (NP20-874). */
  type: BenchType;
  applicant: boolean;
  freelancer: boolean;
  firm: BenchFirm;
  position: string | null;
  location: string | null;
  availableFrom: string | null;
  commitmentFrom: string | null;
  remainingVacationDays: number;
  nextStep: string | null;
  /**
   * Vom Backend vorgerechnete Stundensaetze (NP20-934/946): Kostensatz des
   * Beraters plus die zentralen Parameter aus NP20-933. `null`, solange kein
   * Kostensatz gepflegt ist. Dienen ausschliesslich der Vorbelegung eines neuen
   * Angebots — gespeicherte Angebote tragen ihre eigenen Werte.
   */
  suggestedHourlyRateRemote: number | null;
  suggestedHourlyRateOnSite: number | null;
  offers: OfferDTO[];
  notes: NoteDTO[];
}

/**
 * Stati, in denen ein Zusage-Datum gilt (NP20-797): ab der Zusage bleibt es erhalten, auch wenn das
 * Angebot weiterwandert. Vorher hing alles an ACCEPTED, sodass „Projekt angelegt" das Datum verlor.
 */
export const COMMITTED_STATUSES: readonly RequestStatus[] = ['ACCEPTED', 'PROJECT_CREATED'];

export function hasCommitmentDate(status: RequestStatus | null | undefined): boolean {
  return !!status && COMMITTED_STATUSES.includes(status);
}

/**
 * „Zusage ab" eines Eintrags (NP20-774): das Datum des zugesagten Angebots, sonst der Wert am
 * Eintrag selbst. Übersicht und Detailansicht müssen dieselbe Ableitung nutzen, sonst zeigen sie
 * für denselben Eintrag verschiedene Daten.
 */
export function acceptedCommitmentFrom(entry: Pick<BenchEntryDTO, 'offers' | 'commitmentFrom'>): string | null {
  return (entry.offers ?? []).find(o => hasCommitmentDate(o.status) && o.commitmentFrom)?.commitmentFrom
    ?? entry.commitmentFrom;
}

/** Sales-list member (NP20-792). abbreviation/fullName are projected from the linked user. */
export interface SalesPersonDTO {
  id: string;
  userId: string;
  abbreviation: string;
  fullName: string;
  firm: BenchFirm;
  absenceFrom: string | null;
  absenceUntil: string | null;
  absenceReason: string | null;
  substituteUserId: string | null;
}

/** Selectable user option for the "add member" dropdown (NP20-792). */
export interface SelectableUserDTO {
  id: string;
  fullName: string;
  abbreviation: string;
}

/** Body for adding a member to the sales list (NP20-792). */
export interface AddSalesPersonRequest {
  userId: string;
  firm: BenchFirm;
}

/** Body for updating a sales-list member (NP20-792); userId is immutable and kept server-side. */
export interface UpdateSalesPersonRequest {
  firm: BenchFirm;
  absenceFrom: string | null;
  absenceUntil: string | null;
  absenceReason: string | null;
  substituteUserId: string | null;
}
